import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import {
  Users, TrendingUp, FileText, CheckCircle, Clock, Search, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

export const DashboardCollaboratori = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await api.get('/richieste-emissione/dashboard-collaboratori');
      setData(res.data.collaboratori || []);
    } catch { toast.error('Errore caricamento dashboard'); } finally { setLoading(false); }
  };

  const filtered = data.filter(c => !searchQuery || c.collaboratore_nome.toLowerCase().includes(searchQuery.toLowerCase()));

  // Totals
  const totRichieste = data.reduce((s, c) => s + c.totale, 0);
  const totEmesse = data.reduce((s, c) => s + c.emessa, 0);
  const totPremi = data.reduce((s, c) => s + c.premi_emessi, 0);
  const tassoGlobale = totRichieste > 0 ? Math.round(totEmesse / totRichieste * 100) : 0;

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin h-10 w-10 border-b-2 border-slate-600 rounded-full" /></div>;

  return (
    <div className="p-3 sm:p-6 md:p-8" data-testid="dashboard-collaboratori-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Dashboard Collaboratori</h1>
        <p className="text-sm text-slate-500 mt-0.5">{data.length} collaboratori attivi</p>
      </div>

      {/* Global Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="stat-tot-richieste">
          <div className="flex items-center gap-2 mb-2"><FileText size={16} className="text-slate-400" /><span className="text-xs font-medium text-slate-500">Richieste Totali</span></div>
          <p className="text-3xl font-bold text-slate-900">{totRichieste}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="stat-tot-emesse">
          <div className="flex items-center gap-2 mb-2"><CheckCircle size={16} className="text-green-500" /><span className="text-xs font-medium text-slate-500">Polizze Emesse</span></div>
          <p className="text-3xl font-bold text-green-600">{totEmesse}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="stat-tasso-globale">
          <div className="flex items-center gap-2 mb-2"><TrendingUp size={16} className="text-blue-500" /><span className="text-xs font-medium text-slate-500">Tasso Conversione</span></div>
          <p className="text-3xl font-bold text-blue-600">{tassoGlobale}%</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="stat-tot-premi">
          <div className="flex items-center gap-2 mb-2"><span className="text-slate-400 font-bold text-sm">€</span><span className="text-xs font-medium text-slate-500">Premi Emessi</span></div>
          <p className="text-3xl font-bold text-slate-900">€ {totPremi.toLocaleString('it-IT', { minimumFractionDigits: 0 })}</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm mb-5">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Cerca collaboratore..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-collab" />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="collab-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Collaboratore</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Richieste</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">In Attesa</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">In Lavoraz.</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Emesse</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Rifiutate</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Conversione</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Premi Emessi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={c.collaboratore_id} className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-25'}`} data-testid={`collab-row-${c.collaboratore_id}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Users size={16} className="text-slate-500" />
                      </div>
                      <span className="font-semibold text-sm text-slate-800">{c.collaboratore_nome}</span>
                    </div>
                  </td>
                  <td className="text-center px-4 py-4"><span className="text-sm font-bold text-slate-900">{c.totale}</span></td>
                  <td className="text-center px-4 py-4"><span className="text-sm text-amber-600 font-medium">{c.in_attesa}</span></td>
                  <td className="text-center px-4 py-4"><span className="text-sm text-blue-600 font-medium">{c.presa_in_carico + c.incasso_inviato}</span></td>
                  <td className="text-center px-4 py-4"><span className="text-sm text-green-600 font-bold">{c.emessa}</span></td>
                  <td className="text-center px-4 py-4"><span className="text-sm text-red-500 font-medium">{c.rifiutata}</span></td>
                  <td className="text-center px-4 py-4">
                    <div className="flex items-center justify-center gap-1">
                      <span className={`text-sm font-bold ${c.tasso_conversione >= 50 ? 'text-green-600' : c.tasso_conversione >= 25 ? 'text-amber-600' : 'text-red-500'}`}>
                        {c.tasso_conversione}%
                      </span>
                      {c.tasso_conversione >= 50 ? <ArrowUpRight size={14} className="text-green-500" /> : c.tasso_conversione > 0 ? <ArrowDownRight size={14} className="text-amber-500" /> : null}
                    </div>
                  </td>
                  <td className="text-right px-5 py-4"><span className="text-sm font-bold text-slate-900">€ {c.premi_emessi.toLocaleString('it-IT', { minimumFractionDigits: 0 })}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-12"><p className="text-sm text-slate-400">{searchQuery ? 'Nessun collaboratore trovato' : 'Nessun dato disponibile'}</p></div>
        )}
      </div>
    </div>
  );
};

export default DashboardCollaboratori;
